// The following import loads the remote-config part of the firebase
// library into the firebase namespace.
import {
  fetchAndActivate,
  getAll,
  getBoolean,
  getNumber,
  getRemoteConfig,
  getString,
  getValue,
  LogLevel,
  RemoteConfig,
  setLogLevel,
} from 'firebase/remote-config';

import config from 'config';
import logger from 'helpers/logger';

import { GetConfigGettersOnceLoadedFn, RemoteConfigGetters } from './types';

const ONE_MINUTE_MS = 60 * 1000;
const RESPONSE_DUE_TO_TIMEOUT = 'timeout-response';

let remoteConfig: RemoteConfig;

let hasConfigLoaded = false;
let fetchAndActivatePromise: Promise<unknown>;

const initialize = () => {
  remoteConfig = getRemoteConfig();
  remoteConfig.settings = {
    fetchTimeoutMillis: ONE_MINUTE_MS,
    // The minimum time between updates of the remote config. This value
    // should not be set too low in production in order to avoid a throttling
    // error.
    // See https://firebase.google.com/docs/remote-config/use-config-web#throttling
    minimumFetchIntervalMillis: config.remoteConfigMinFetchIntervalMs,
  };

  setLogLevel(remoteConfig, config.remoteConfigLogLevel as LogLevel);

  remoteConfig.defaultConfig = config.remoteConfigDefaults;
};

/**
 * Fetches the latest remote config and immediately sets it as the currently
 * active state.
 *
 * Currently, this should only be called when the user first loads the app -
 * potentially before they've logged in. This gives time for the latest remote
 * config to load before we're likely to show any branched views. Currently,
 * the remote config values should generally be loaded via the getConfigGettersOnceLoaded
 * function however as this means users aren't shown one A:B test before the
 * remote config switches them to being under another.
 * This corresponds to "Strategy 2: Activate behind loading screen" in the docs:
 * https://firebase.google.com/docs/remote-config/loading#strategy_2_activate_behind_loading_screen
 */
export const initializeFetchAndActivate = async () => {
  initialize();
  if (remoteConfig === undefined) {
    throw new Error('The remoteConfig property should be initialized');
  }
  try {
    fetchAndActivatePromise = fetchAndActivate(remoteConfig);
    await fetchAndActivatePromise;
  } catch (e) {
    if (e === 'FETCH_THROTTLE') {
      // Log the issue so we know it's occurring but continue with the cached/default config
      // TODO(matt): Consider adding an exponential backoff based retry as
      // recommended in the docs
      logger.error(e);
    } else {
      throw e;
    }
  }
  hasConfigLoaded = true;
};

const produceRemoteConfigGetters = (): RemoteConfigGetters => {
  if (remoteConfig === undefined) {
    throw new Error('The remoteConfig property should be initialized');
  }

  // As remoteConfig is a class instance, just returning { getX, getY, ...} directly
  // from remoteConfig causes errors as they are separated from their instance.
  return {
    getAll: () => getAll(remoteConfig),
    getBoolean: (key: string) => getBoolean(remoteConfig, key),
    getNumber: (key: string) => getNumber(remoteConfig, key),
    getString: (key: string) => getString(remoteConfig, key),
    getValue: (key: string) => getValue(remoteConfig, key),
  };
};

// Note that any users of this module will need to handle this
// promise possibly resolving after an unmount.
const waitForTimeout = (timeoutMs: number) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(RESPONSE_DUE_TO_TIMEOUT), timeoutMs);
  });

/**
 * Only returns the current remote config once the initial startup
 * fetch has been completed.
 */
export const getConfigGettersOnceLoaded: GetConfigGettersOnceLoadedFn = async (
  maxTimeoutMs = undefined,
) => {
  // TODO(matt): Consider going for a different strategy which allows using the
  // default or currently stored config with fresh values reloaded for next time.
  // TODO(matt): Change the logic in initializeFetchAndActivate so that if the timeout
  // is triggered here before the config has loaded, do not activate the config on
  // this page load so that the user maintains a consistent view for this visit.
  if (hasConfigLoaded) {
    return {
      ...produceRemoteConfigGetters(),
      isFromCacheDueToTimeout: false,
    };
  }
  let isFromCacheDueToTimeout = false;
  if (maxTimeoutMs === undefined) {
    await fetchAndActivatePromise;
  } else {
    const response = await Promise.race([
      fetchAndActivatePromise,
      waitForTimeout(maxTimeoutMs),
    ]);
    if (response === RESPONSE_DUE_TO_TIMEOUT) {
      isFromCacheDueToTimeout = true;
    }
  }
  return {
    ...produceRemoteConfigGetters(),
    isFromCacheDueToTimeout,
  };
};
